import React from 'react';
import Tooltip from './Tooltip';
import { Link } from 'react-router-dom';

export default function Tooltips({id, left, top}) {
	if(!tips[id]){ return null; }

	return (
		<Tooltip id={id} left={left} top={top}>
			<Content id={id} />
		</Tooltip>
	);
}

function Content({id, outerClass, showLink = true}){
	let {title, description, image, imageWidth} = tips[id];
	let styles = {};
	if(showLink && imageWidth === '500px'){
		styles.flexDirection = 'column';
	}
	return (
		<div className={outerClass}>
			<div className="tip-title">{title}</div>
			<div className="tip-horizontal" style={styles}>
				<div className="tip-description">
					{description}
					{showLink &&
						<div style={{textAlign:'right', marginTop:'20px'}}>
							<Link to={`/tips#${id}`} target="_blank">Learn More</Link>
						</div>
					}
				</div>
				<div>
					{image && <img alt={title} src={image} style={{width:imageWidth}} />}
				</div>
			</div>
		</div>
	);
}

const tips = {

	screenSize:{
		title: 'Setting the Width and Height of an Experiment',
		image: '/images/screen-size.gif',
		imageWidth:'200px',
		description:(
		<div>
			The Screen Size controls the size of all the screens in an experiment.
			<br /><br />
			In order for an experiment to display optimally on the most devices, it is suggested to keep the screen size smaller rather than bigger.
			<br /><br />
			It is also suggested to set the screen size before building your experiment to avoid having to reposition elements.   
		</div>)
	},

	leftRail: {
		title: 'Left Rail',
		image: '/images/left-rail.gif',
		description:(
		<div>
			The left rail is used to organize, group, duplicate, and delete screens.
			<ul>
				<li>Multiple screens can be selected by using Cmd + Click</li>
				<li>Duplicate a screen by right clicking.</li>
				<li>Group screens together by right clicking.</li>
			</ul>
		</div>)
	},
	
	manageMedia:{
		title: 'Using the Media Manager',
		image: '/images/media-manager.gif',
		imageWidth:'500px',
		description:(
		<div>
			The Media Manager is used to upload and identify media assets used in your experiment.<br /><br />
			<b>Preloading:</b> All of the media assets uploaded to the Media Manager will be loaded into an experiment <i>before</i> an experiment starts. This ensures timings will be more accurate and not dependent on network speeds.<br /><br />
			<b>Unique Ids:</b> Since assets are referenced in experiments by an id, the ids need to be unique.<br /><br />
			<b>Supported formats:</b> Images: jpg, png, gif. Audio: mp3. Videos: mp4, ogg, webm<br /><br />
			<b>Uploading The Same File:</b> In order to easily update images and re-upload them, if you re-use the same filename, it will replace the existing image.
		</div>)
	},

	id:{
		title: 'The ID Property (Screens and Groups)',
		description:(
		<div>
			The <b>ID</b> should be unique. It is used to identify a screen or group in the responses of participants.
		</div>)
	},

	dataset: {
		title: 'Datasets (Group Properties)',
		image: '/images/datasets.gif',
		imageWidth: '500px',
		description:(<div>
			Datasets allow you to dynamically load data into the elements of any child screen. For every row in a Dataset, the experiment will loop over the Group's children.
			<br /><br />
			Datasets can be created/accessed via clicking on a "Group" in the left rail, and the selecting a data set in the right rail. <Link to='/tips#leftRail'>(Learn how to create groups here.)</Link>
			<br /><br />
			The Dataset table supports adding and removing rows/columns by right clicking.
			<br /><br />
			To use a cell value in an Element property, add the desired field in curly braces. For example, <i>{'{'}row.option1{'}'}</i> would be replaced by the cell value under the header "option1" in the Dataset table.
		</div>)
	},

	randomizeChildren: {
		title: 'Randomize Children (Group Properties)',
		image: '/images/randomize-children.png',
		imageWidth: '200px',
		description:(<div>
			This option will randomize the direct children of the selected Group. 
			<br /><br />
			In this example, "Experiment" is selected, and the three green arrows indicate the screens that will be randomized. Note that the screens inside of "New Group" will not be randomized, because they are not direct children of "Experiment".
			<br /><br />
			This option is available in the right rail by clicking on a "Group" in the left rail.
		</div>)
	},

	expectedValue:{
		title: 'Setting an Expected Value (Element Properties)',
		image: '/images/expected-value.png',
		imageWidth:'200px',
		description:(
		<div>
			You can add an expected value to be included with your responses. This allows for easier post-processing of response data to check for correctness.
		</div>)
	},

	onClick:{
		title: 'The OnClick Property (Element Properties)',
		//image: '/images/left-rail.gif',
		//imageWidth:'200px',
		description:(
		<div>
			The onClick property is an optional field. It is executed after an element is clicked.
			It supports:
			<ul>
				<li><b>{'{'}next{'}'}</b> : advance to the next screen or group</li>
				<li><b>{'{'}finish{'}'}</b> : immediately finish the experiment</li>
			</ul>
		</div>)
	},

	elementId:{
		title: 'The Element ID Property (Element Properties)',
		description:(
		<div>
			The <b>ID</b> is used to identify elements in responses. In general, they should be unique.<br /><br />
			Note: If you have multiple elements that are all related to a singular response, you can assign the same <b>ID</b> to all of them. Whichever element is clicked last will be preserved in the final value for the <b>ID</b> in the responses.
			<br /><br />
			For example, if you have 3 buttons with the same <b>ID</b> and all three are clicked, only the final one's value will be saved in the Group's row[]response value.
		</div>)
	},

	elementURL:{
		title: 'The Image Id/Url Property (Element Properties)',
		description:(
		<div>
			You can use a url or an id from the Media Manager. It is recommended to use an id from the Media Manager since all the media assets in the media manager are loaded prior to the experiment starting.
			<br /><br />
			You can also use a variable to reference a value from the current Dataset.
			<br /><br />
			For example, if a Dataset has a column with a header name of "pic", and one of the rows has a value of "chart1", you can reference the media asset with id "chart1" by using the value <b>{'{'}row.pic{'}'}</b>.
		</div>)
	},

	elementOnEnd:{
		title: 'Using the onEnd Property (Element Properties)',
		description:(
		<div>
			The onEnd property is executed when the media finishes.<br /><br />
			It supports:
			<ul>
				<li><b>{'{'}next{'}'}</b> : advance to the next screen or group</li>
				<li><b>{'{'}finish{'}'}</b> : immediately finish the experiment</li>
			</ul>
		</div>)
	},

	elementOptions:{
		title: 'The Options Property (Element Properties)',
		description:(
		<div>
			Enter an option per line. Blank lines are allowed.
			<br /><br />
			In dropdowns, it is recommended to use a blank line as the first value so that it is clear users responded to the question.
		</div>)
	},

	autoAdvance:{
		title: 'Auto Advance (Element Properties)',
		description:(
		<div>
			If set, the screen will automatically advance to the next screen after X seconds.
		</div>)
	},

	contactParticipant:{
		title: 'Contact A Participant',
		image: '/images/contact-participant.gif',
		imageWidth: '300px',
		description:(
		<div>
			As a researcher, you may contact any participant who:
			<ul>
				<li>has participated in any of your experiments</li>
				<li>AND has opt-ed in to be contacted by researchers on the "My Profile" page</li>
			</ul>
			<br />
			To send a message, you will need the participant's message token. It can be found in the CSV file downloaded on the Experiment Results page. 
			<br /><br />
			The <i>Contact Participants</i> link is located at the top of the Experiment Results page.
		</div>)
	},

	participantCap:{
		title: 'Participant Cap (Publish Details)',
		description:(
		<div>
			Sets the maximum number of partipants you would like to participate in your experiment. Once the cap has been met, the experiment will no longer be displayed to participants.
		</div>)
	},

	incentive:{
		title: 'Incentive (Publish Details)',
		description:(
		<div>
			The amount in US dollars that you agree to pay users who participate in your experiment.
		</div>)
	},

	status:{
		title: 'Publishing Experiments (Publish Details)',
		description:(
		<div>
			An experiment can be published by setting the <b>status</b> to <b>active</b>. It will then be displayed to all participants who match the audience rules defined in the publish details.
			<br /><br />
			The status can be one of the following:
			<ul>
				<li><b>Active</b>: The experiment is published and available to users, as long as the cap has not been met.</li>
				<li><b>Inactive</b>: The experiment is not available to all users. Usually experiments are inactive if they are being created, or if they are paused.</li>
				<li><b>Archived</b>: Experiments can be archived if they are no longer being used. Archived experiments are no longer visible to participants.<br />Note: Once archived, experiments do not show up under the "All" filter on the My Experiments page.</li>
			</ul>
			<br /><br />
			The status can be swapped back and forth between the different values. For example, an archived experiment can be activated if desired.
		</div>)
	},

	rules:{
		title: 'Audience Rules (Publish Details)',
		description:(
		<div>
			The Audience Rules are used to target your experiment to a filtered audience. By adding more rules, you reduce the number of potential users who can take your participant.
			<br /><br />
			All the rules are ANDed together except for when the same field is used twice. Those rules are ORed together. 
			<br /><br />
			For example, if city = Saratoga, gender = male, and gender = female, the resulting rule will be: city = Saratoga AND ( gender = male OR gender = female).
		</div>)
	},

};

Tooltips.tips = tips;
Tooltips.Content = Content;



// WEBPACK FOOTER //
// ./src/components/Tooltips.js